import { FileData, spLogTags } from "@eventr/common";

export const MAX_IMAGE_DIMENSION = 1600;

export function base64ToFile(base64: string, mimeType: string, filename: string): File {
  const bstr = atob(base64);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mimeType });
}

export function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === "string") {
        resolve(reader.result);
      } else {
        reject(new Error("Unexpected result type"));
      }
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

export const formatFileSize = (sizeInBytes: number): string => {
  if (sizeInBytes < 1024) return `${sizeInBytes} B`;
  if (sizeInBytes < 1024 * 1024) return `${(sizeInBytes / 1024).toFixed(2)} KB`;
  return `${(sizeInBytes / 1024 / 1024).toFixed(2)} MB`;
};

export const formatImageFileName = (extension: string) => {
  return `IMG-${Date.now()}.${extension}`;
};

export async function processFileForUpload(file: File | Blob): Promise<FileData> {
  if (file.type.startsWith("image/")) {
    try {
      spLogTags(`[file] Processing image creating bitmap`, ["expenses"]);
      const bitmap = await createImageBitmap(file);
      let { width, height } = bitmap;

      if (width > MAX_IMAGE_DIMENSION || height > MAX_IMAGE_DIMENSION) {
        if (width > height) {
          height = Math.round((height * MAX_IMAGE_DIMENSION) / width);
          width = MAX_IMAGE_DIMENSION;
        } else {
          width = Math.round((width * MAX_IMAGE_DIMENSION) / height);
          height = MAX_IMAGE_DIMENSION;
        }
      }

      const canvas = new OffscreenCanvas(width, height);
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        throw new Error("Failed to get canvas context");
      }
      ctx.drawImage(bitmap, 0, 0, width, height);
      bitmap.close();

      spLogTags(`[file] Converting canvas to blob`, ["expenses"]);
      const blob = await canvas.convertToBlob({ type: "image/jpeg", quality: 0.9 });
      const base64 = (await blobToDataUrl(blob)).split(",")[1] ?? "";
      const extension = "jpeg";

      spLogTags(`[file] Processing complete returning file`, ["expenses"]);
      return {
        base64,
        mimeType: "image/jpeg",
        filename: formatImageFileName(extension),
      };
    } catch (e) {
      spLogTags(`e ${e}`, ["Expenses"]);
      const base64 = (await blobToDataUrl(file)).split(",")[1] ?? "";
      return {
        base64,
        mimeType: file.type,
        filename: formatImageFileName(file.type.split("/")[1] ?? "bin"),
      };
    }
  }

  const base64 = (await blobToDataUrl(file)).split(",")[1] ?? "";
  const extension = file.type.split("/")[1] ?? "";

  return {
    base64,
    mimeType: file.type,
    filename: formatImageFileName(extension),
  };
}

export function logFilePicked(fileData: FileData) {
  spLogTags(`[file] File picked ${fileData.mimeType} approximate size ${formatBase64Size(fileData.base64)}`, [
    "expenses",
  ]);
}

export function formatBase64Size(base64: string) {
  const padding = (base64.match(/=*$/) || [""])[0].length;
  const bytes = Math.ceil((base64.length * 3) / 4) - padding;

  if (bytes < 1024) {
    return `${bytes} B`;
  }

  const kilobytes = bytes / 1024;

  if (kilobytes < 1024) {
    return `${kilobytes.toFixed(1)} KB`;
  }

  const megabytes = kilobytes / 1024;

  return `${megabytes.toFixed(1)} MB`;
}

export const isFile = (val: unknown): val is File =>
  val !== null &&
  typeof val === "object" &&
  typeof (val as File).name === "string" &&
  typeof (val as File).size === "number" &&
  typeof (val as File).type === "string" &&
  typeof (val as File).lastModified === "number";
